import { useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronRight, CheckCircle, Clock, AlertTriangle, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type LifecycleStage =
  | 'Acquisition' | 'Under Renovation' | 'Renovation Complete'
  | 'Listed & Leasing' | 'Occupied' | 'Under Management' | 'For Sale'

type Milestone = { date: string; event: string; completed: boolean; amount?: number }

type Property = {
  id: string; name: string; address: string; beds: number; baths: number
  purchasePrice: number; currentValue: number; arv: number
  equity: number; totalInvested: number; stage: LifecycleStage
  acquiredDate: string; milestones: Milestone[]
  tenant?: string; monthlyRent?: number; roi?: number
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const PROPERTIES: Property[] = [
  {
    id: 'p1', name: 'Maple Grove House', address: '142 Maple Grove Dr, Austin TX 78701',
    beds: 3, baths: 2, purchasePrice: 185000, currentValue: 220000, arv: 235000,
    equity: 42000, totalInvested: 178000, stage: 'Under Management',
    acquiredDate: '2024-03-15', tenant: 'Jordan Blake', monthlyRent: 2850, roi: 8.4,
    milestones: [
      { date: '2024-03-15', event: 'Property Acquired', completed: true, amount: 185000 },
      { date: '2024-04-01', event: 'Renovation Started', completed: true, amount: 18500 },
      { date: '2024-06-20', event: 'Renovation Complete', completed: true },
      { date: '2024-07-01', event: 'Listed for Rent', completed: true },
      { date: '2024-07-18', event: 'Tenant Move-In (Jordan Blake)', completed: true },
      { date: '2025-01-01', event: 'First Lease Renewal Offered', completed: true, amount: 2850 },
      { date: '2026-07-01', event: 'Annual Inspection Due', completed: false },
      { date: '2026-12-31', event: 'Current Lease Expiration', completed: false },
    ],
  },
  {
    id: 'p2', name: 'Willow Creek House', address: '88 Willow Creek Blvd, Austin TX 78704',
    beds: 2, baths: 1, purchasePrice: 162000, currentValue: 214000, arv: 225000,
    equity: 52000, totalInvested: 162000, stage: 'Occupied',
    acquiredDate: '2024-08-10', tenant: 'Avery Kim', monthlyRent: 3100, roi: 9.2,
    milestones: [
      { date: '2024-08-10', event: 'Property Acquired', completed: true, amount: 162000 },
      { date: '2024-08-15', event: 'Minor Repairs Completed', completed: true, amount: 4200 },
      { date: '2024-09-01', event: 'Listed for Rent', completed: true },
      { date: '2024-09-22', event: 'Tenant Move-In (Avery Kim)', completed: true },
      { date: '2025-03-01', event: 'Lease Renewed', completed: true, amount: 3100 },
      { date: '2026-07-07', event: 'Late Rent Follow-up In Progress', completed: false },
      { date: '2026-09-01', event: 'Consider Refinance Review', completed: false },
    ],
  },
  {
    id: 'p3', name: 'Cedar Ridge House', address: '317 Cedar Ridge Way, Austin TX 78745',
    beds: 3, baths: 2, purchasePrice: 195000, currentValue: 229000, arv: 245000,
    equity: 34000, totalInvested: 195000, stage: 'Under Management',
    acquiredDate: '2024-11-01', tenant: 'Sam Porter', monthlyRent: 2400, roi: 7.8,
    milestones: [
      { date: '2024-11-01', event: 'Property Acquired', completed: true, amount: 195000 },
      { date: '2024-11-10', event: 'Paint & Flooring Refresh', completed: true, amount: 8500 },
      { date: '2024-12-01', event: 'Listed for Rent', completed: true },
      { date: '2025-01-15', event: 'Tenant Move-In (Sam Porter)', completed: true },
      { date: '2025-02-01', event: 'Lease Signed (12 months)', completed: true, amount: 2400 },
      { date: '2025-12-01', event: 'Lease Renewal Sent', completed: true, amount: 2496 },
      { date: '2026-01-31', event: 'Current Lease Expiration', completed: false },
      { date: '2026-02-15', event: 'Refinance or Sale Decision', completed: false },
    ],
  },
]

const ALL_STAGES: LifecycleStage[] = [
  'Acquisition', 'Under Renovation', 'Renovation Complete',
  'Listed & Leasing', 'Occupied', 'Under Management', 'For Sale',
]

const STAGE_COLOR: Record<LifecycleStage, string> = {
  'Acquisition': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  'Under Renovation': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  'Renovation Complete': 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-800',
  'Listed & Leasing': 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800',
  'Occupied': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  'Under Management': 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-800',
  'For Sale': 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
const pct = (n: number, d: number) => `${((n / d) * 100).toFixed(1)}%`

// ─── Portfolio Summary ────────────────────────────────────────────────────────

function PortfolioSummary() {
  const totalValue = PROPERTIES.reduce((s, p) => s + p.currentValue, 0)
  const totalInvested = PROPERTIES.reduce((s, p) => s + p.totalInvested, 0)
  const totalEquity = PROPERTIES.reduce((s, p) => s + p.equity, 0)
  const totalRent = PROPERTIES.reduce((s, p) => s + (p.monthlyRent ?? 0), 0)
  const avgROI = (PROPERTIES.reduce((s, p) => s + (p.roi ?? 0), 0) / PROPERTIES.length).toFixed(1)

  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
      {[
        { label: 'Portfolio Value', value: fmt(totalValue), delta: 'Current estimate', tone: 'up' },
        { label: 'Total Invested', value: fmt(totalInvested), delta: 'Acquisition cost', tone: 'neutral' },
        { label: 'Total Equity', value: fmt(totalEquity), delta: `${pct(totalEquity, totalValue)} of value`, tone: 'up' },
        { label: 'Monthly Income', value: fmt(totalRent), delta: '3 occupied units', tone: 'up' },
        { label: 'Avg Portfolio ROI', value: `${avgROI}%`, delta: 'Blended return', tone: 'up' },
      ].map(card => (
        <Card key={card.label} className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{card.label}</p>
          <p className={cn('mt-1.5 text-xl font-bold leading-none',
            card.tone === 'up' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-900 dark:text-slate-100')}>{card.value}</p>
          <p className="text-xs text-slate-500 mt-1.5">{card.delta}</p>
        </Card>
      ))}
    </div>
  )
}

// ─── Property Card ────────────────────────────────────────────────────────────

function PropertyLifecycleCard({ prop }: { prop: Property }) {
  const [expanded, setExpanded] = useState(false)
  const completedCount = prop.milestones.filter(m => m.completed).length
  const progressPct = Math.round((completedCount / prop.milestones.length) * 100)
  const stageIdx = ALL_STAGES.indexOf(prop.stage)
  const gain = prop.currentValue - prop.totalInvested

  return (
    <Card className="shadow-sm overflow-hidden">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">{prop.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{prop.address}</p>
              <p className="text-xs text-slate-500">{prop.beds}BR / {prop.baths}BA · Acquired {prop.acquiredDate}</p>
            </div>
            <span className={cn('rounded-full border px-3 py-1 text-xs font-bold', STAGE_COLOR[prop.stage])}>{prop.stage}</span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4 text-sm">
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2.5">
              <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Purchase</p>
              <p className="font-bold text-slate-900 dark:text-slate-100 mt-0.5">{fmt(prop.purchasePrice)}</p>
            </div>
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 p-2.5">
              <p className="text-[11px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wide font-semibold">Current Value</p>
              <p className="font-bold text-emerald-700 dark:text-emerald-400 mt-0.5">{fmt(prop.currentValue)}</p>
              <p className="text-[10px] text-emerald-600 dark:text-emerald-500">+{fmt(gain)} gain</p>
            </div>
            <div className="rounded-lg bg-violet-50 dark:bg-violet-950/20 p-2.5">
              <p className="text-[11px] text-violet-600 dark:text-violet-400 uppercase tracking-wide font-semibold">Equity</p>
              <p className="font-bold text-violet-700 dark:text-violet-400 mt-0.5">{fmt(prop.equity)}</p>
              <p className="text-[10px] text-violet-500">{pct(prop.equity, prop.currentValue)} of value</p>
            </div>
            <div className="rounded-lg bg-sky-50 dark:bg-sky-950/20 p-2.5">
              <p className="text-[11px] text-sky-600 dark:text-sky-400 uppercase tracking-wide font-semibold">ROI</p>
              <p className="font-bold text-sky-700 dark:text-sky-400 mt-0.5">{prop.roi}%</p>
              <p className="text-[10px] text-sky-500">{fmt(prop.monthlyRent ?? 0)}/mo rent</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Lifecycle Progress</p>
              <p className="text-xs text-slate-500">{completedCount}/{prop.milestones.length} milestones</p>
            </div>
            <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-violet-600"
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="mt-2 flex gap-0.5 overflow-x-auto">
              {ALL_STAGES.map((stage, i) => (
                <div key={stage} className={cn(
                  'h-1.5 flex-1 rounded-full min-w-[12px]',
                  i < stageIdx ? 'bg-violet-600' : i === stageIdx ? 'bg-violet-400' : 'bg-slate-200 dark:bg-slate-700'
                )} title={stage} />
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
        >
          <span>{expanded ? 'Hide' : 'Show'} Milestone Timeline</span>
          <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
        </button>

        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="px-4 pb-4">
            <div className="relative space-y-0 ml-3 border-l-2 border-slate-200 dark:border-slate-700 pl-5">
              {prop.milestones.map((m, i) => (
                <div key={i} className="relative pb-3 last:pb-0">
                  <div className={cn(
                    'absolute -left-[25px] h-4 w-4 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center',
                    m.completed ? 'bg-violet-600' : 'bg-slate-300 dark:bg-slate-600'
                  )}>
                    {m.completed && <CheckCircle className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={cn('text-xs font-semibold', m.completed ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400')}>
                        {m.event}
                      </p>
                      <p className="text-[11px] text-slate-400">{m.date}</p>
                    </div>
                    {m.amount && (
                      <span className={cn('text-[11px] font-bold rounded-full px-2 py-0.5 border',
                        m.completed ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800' :
                        'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-600'
                      )}>{fmt(m.amount)}</span>
                    )}
                    {!m.completed && (
                      <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800 rounded-full px-2 py-0.5 font-semibold">
                        Upcoming
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Action Items ─────────────────────────────────────────────────────────────

function ActionItems() {
  const [items, setItems] = useState([
    { priority: 'urgent', property: 'Willow Creek House', action: 'Follow up on 12-day late rent ($3,100)', due: 'Today', icon: AlertTriangle, iconColor: 'text-rose-600' },
    { priority: 'normal', property: 'Maple Grove House', action: 'Conduct annual property inspection', due: 'Jul 31', icon: Clock, iconColor: 'text-amber-600' },
    { priority: 'normal', property: 'Maple Grove House', action: 'Send lease renewal notice to Jordan Blake', due: 'Aug 15', icon: Clock, iconColor: 'text-amber-600' },
    { priority: 'low', property: 'Cedar Ridge House', action: 'Decide on lease renewal or re-listing for Jan 31 expiry', due: 'Nov 1', icon: TrendingUp, iconColor: 'text-violet-600' },
    { priority: 'low', property: 'Willow Creek House', action: 'Review refinance options — rates favorable', due: 'Q3 2026', icon: TrendingUp, iconColor: 'text-violet-600' },
  ])

  const completeAction = (index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index))
  }

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">Portfolio Action Items</CardTitle>
        <span className="text-xs font-semibold text-rose-600 bg-rose-50 dark:bg-rose-950/30 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-full px-2.5 py-1">
          {items.filter(i => i.priority === 'urgent').length} urgent
        </span>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((item, i) => {
            const Icon = item.icon
            return (
              <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', item.iconColor)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.action}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{item.property}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={cn('text-[11px] font-semibold rounded-full border px-2.5 py-0.5',
                    item.priority === 'urgent' ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800' :
                    item.priority === 'normal' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800' :
                    'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-600'
                  )}>{item.due}</span>
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => completeAction(i)}>Mark Done</Button>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

type LifecycleTab = 'overview' | 'actions'

export default function LifecycleModule() {
  const [tab, setTab] = useState<LifecycleTab>('overview')

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Property Lifecycle</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">Acquire → Renovate → Lease → Manage → Maintain → Sell</p>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        {([['overview', 'Portfolio Overview'] as const, ['actions', 'Action Items'] as const]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={cn(
              'rounded-lg px-3 py-2 text-sm font-medium transition-all',
              tab === id ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/60'
            )}>{label}</button>
        ))}
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
        {tab === 'overview' ? (
          <div className="space-y-4">
            <PortfolioSummary />
            {PROPERTIES.map(p => <PropertyLifecycleCard key={p.id} prop={p} />)}
          </div>
        ) : (
          <ActionItems />
        )}
      </motion.div>
    </div>
  )
}
